/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { NumberField, Text, useFlags } from 'sarsaparilla';
import PassTypesTable from 'ui-sitepass/src/components/PassTypesTable.jsx';
import LEGACY_PassTypesTable from 'ui-sitepass/src/components/LEGACY_PassTypesTable';
import { PassTypeProps } from 'ui-sitepass/src/props';
import { getPrice } from 'ui-sitepass/src/utils';
import { durationLength } from 'ui-sitepass/src/utils/duration_utils';
import { getValidPriceFromDivision } from 'ui-sitepass/src/utils/date_time_utils';

function SitePassSelection({
    selectedPassTypeId,
    passTypeId,
    passTypes,
    passCounts,
    maxTotalGuestsReached,
    pricing,
    onChange,
    sortFn,
    onSelectPass,
    onRemovePass,
    min,
    max,
    facilityId,
    reservations,
}) {
    const { sitePassUiRevamp } = useFlags();
    const showNewSitePassLayout = useMemo(() => {
        return sitePassUiRevamp;
    }, [sitePassUiRevamp]);

    //Filter to only return non-commercial passes
    const filteredStandardRawPassTypes = useMemo(
        () => passTypes.filter((item) => !item?.features?.['is-commercial-pass']),
        [passTypes]
    );

    const handleChangeCounter = useCallback(
        (id) => (value) => {
            onChange(id, value);
        },
        [onChange]
    );

    const renderPassesListItem = ({ id, name, usedFor, duration, count, price }) => {
        return (
            <li className="site-pass-selection__list-item" key={`pass-type-${id}`}>
                {id === passTypeId && (
                    <div className="site-pass-selection__top-featured">
                        <Text color="white" fontWeight="semibold" size="sm">
                            Most people buy this pass
                        </Text>
                    </div>
                )}
                <div className="site-pass-selection__item">
                    <div className="site-pass-selection__left-side">
                        <Text fontWeight="semibold" size="lg">
                            {name}
                        </Text>
                        <Text size="sm">{usedFor}</Text>
                    </div>
                    <div className="site-pass-selection__right-side">
                        <div className="site-pass-selection__right-side-item">
                            <Text align="center" size="sm">
                                Duration
                            </Text>
                            <Text align="center" fontWeight="semibold">
                                {durationLength(duration)}
                            </Text>
                        </div>
                        <div className="site-pass-selection__right-side-item">
                            <Text align="center" size="sm">
                                Price
                            </Text>
                            <Text align="center" fontWeight="semibold">
                                {price}
                            </Text>
                        </div>
                        <NumberField
                            id={`pass-type-counter-${id}`}
                            className="site-pass-selection__right-side-item"
                            units="passes"
                            visualLabel=""
                            min={min}
                            max={max}
                            isIncreaseDisabled={maxTotalGuestsReached}
                            value={count}
                            defaultValue={count}
                            onChange={handleChangeCounter(id)}
                        />
                    </div>
                </div>
            </li>
        );
    };

    const renderPasses = () => {
        return passTypes
            ?.sort(sortFn)
            ?.map(({ id, name, periods, used_for: usedFor, duration }) => {
                const count = passCounts[id];
                const price = getValidPriceFromDivision(periods, moment());
                const passType = { id, name, periods, usedFor, duration, count, price };

                return renderPassesListItem(passType);
            });
    };

    if (!passTypes?.length) {
        return null;
    }

    if (showNewSitePassLayout) {
        return (
            <PassTypesTable
                selectedPassTypeId={selectedPassTypeId}
                passTypes={filteredStandardRawPassTypes}
                passTypesRaw={filteredStandardRawPassTypes}
                pricing={pricing}
                onChange={onChange}
                onSelectPass={onSelectPass}
                onRemovePass={onRemovePass}
                showDescription={false}
            />
        );
    }

    if (!showNewSitePassLayout) {
        return (
            <LEGACY_PassTypesTable
                passTypeId={passTypeId}
                passTypes={passTypes}
                passCounts={passCounts}
                reservations={reservations}
                maxTotalGuestsReached={maxTotalGuestsReached}
                pricing={pricing}
                onChange={onChange}
                facilityId={facilityId}
            />
        );
    }

    return <ul className="site-pass-selection">{renderPasses()}</ul>;
}

SitePassSelection.propTypes = {
    selectedPassTypeId: PropTypes.string,
    passTypeId: PropTypes.string,
    passTypes: PropTypes.arrayOf(PassTypeProps),
    passCounts: PropTypes.object,
    maxTotalGuestsReached: PropTypes.bool,
    pricing: PropTypes.node,
    onChange: PropTypes.func,
    sortFn: PropTypes.func,
    onSelectPass: PropTypes.func,
    onRemovePass: PropTypes.func,
    min: PropTypes.number,
    max: PropTypes.number,
    facilityId: PropTypes.string,
    reservations: PropTypes.arrayOf(PropTypes.object),
};

SitePassSelection.defaultProps = {
    selectedPassTypeId: '',
    passTypeId: '',
    passTypes: [],
    passCounts: {},
    maxTotalGuestsReached: false,
    pricing: null,
    onChange: () => {},
    sortFn: (a, b) =>
        parseFloat(getPrice(b.periods).replace(/\/.*|\D/g, ' ')) -
        parseFloat(getPrice(a.periods).replace(/\/.*|\D/g, ' ')),
    onSelectPass: () => {},
    onRemovePass: () => {},
    min: 0,
    max: 999,
};

export default SitePassSelection;
